import { inject, Injectable } from '@angular/core';
import { type Params } from '@angular/router';
import { type HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { type Action } from '@ngrx/store';
import { type BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';

import { type SitePage, type SitePageEntity } from '@common/types/site-page.type';

import { ExitPageModalComponent } from '@web/shared/modals/exit-page-modal';
import {
    CTA_AFTER_PUBLISH_MODAL_STORAGE_KEY,
    CtaAfterPublishModalComponent,
} from '@web/shared/modals/cta-after-publish-modal/cta-after-publish-modal.component';

import { PagesHttpService } from '@web/store/website/pages/pages.http.service';
import { SiteHttpService } from '@web/store/website/site/site.http.service';

import { PagesService } from '@web/store/website/pages/pages.service';
import { SiteService } from '@web/store/website/site/site.service';
import { OnboardingWizardAbTestService } from '@web/core/services/onboarding-wizard-ab-test.service';

import { RouterFacade } from '@web/store/router/router.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { ModalsFacade } from '@web/store/modals/modals.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { ContentFacade } from '@web/store/website/content/content.facade';
import { UserFacade } from '@web/store/user/user.facade';
import { AppFacade } from '@web/store/app/app.facade';

import NotifyAction from '@common/utils/notify/notify.actions';
import BlocksActions from '@web/store/page-structure/blocks/blocks.actions';
import ContentActions from '@web/store/website/content/content.actions';
import SiteActions from '@web/store/website/site/site.actions';
import PagesActions from './pages.actions';
import { EContentSources } from '@common/enums';

@Injectable()
export class PagesEffects {
    private readonly actions$ = inject(Actions);
    private readonly translateService = inject(TranslateService);
    private readonly siteService = inject(SiteService);
    private readonly pagesService = inject(PagesService);
    private readonly modalService = inject(BsModalService);
    private readonly routerFacade = inject(RouterFacade);
    private readonly siteFacade = inject(SiteFacade);
    private readonly pagesFacade = inject(PagesFacade);
    private readonly contentFacade = inject(ContentFacade);
    private readonly modalsFacade = inject(ModalsFacade);
    private readonly userFacade = inject(UserFacade);
    private readonly appFacade = inject(AppFacade);
    private readonly onboardingWizardAbTestService = inject(OnboardingWizardAbTestService);
    private readonly siteHttpService = inject(SiteHttpService);
    private readonly pagesHttpService = inject(PagesHttpService);

    public readonly navigateSitePage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PagesActions.navigateSitePage),
            filter(({ skipContentChange }) => !skipContentChange),
            map(({ siteId, pageId }) => {
                return ContentActions.loadPageContentRequest({ siteId, pageId });
            })
        )
    );

    public readonly navigateSitePageFromDrawer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PagesActions.navigateSitePageFromDrawer),
            debounceTime(150),
            withLatestFrom(
                this.contentFacade.isDirty$,
                this.routerFacade.routerParams$,
                ({ siteId, pageId }, isDirty, routerParams) => ({
                    siteId,
                    pageId,
                    isDirty,
                    routerParams,
                })
            ),
            map(({ isDirty, pageId, siteId, routerParams }) => {
                if (!isDirty) {
                    return PagesActions.navigateSitePage({ siteId: siteId, pageId: pageId });
                }

                return SiteActions.saveSite({
                    exit: false,
                    publish: false,
                    navigateTo: { pageId, siteId },
                    skipContentChange: false,
                    capturedPageId: routerParams.pageId,
                });
            })
        )
    );

    public readonly addNewPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PagesActions.addNewPage),
            withLatestFrom(
                this.contentFacade.isDirty$,
                this.routerFacade.routerParams$,
                (_, isDirty, routerParams) => ({
                    isDirty,
                    routerParams,
                })
            ),
            switchMap(({ isDirty, routerParams }) => {
                if (!isDirty) {
                    return [PagesActions.createNewPage()];
                }

                return [
                    SiteActions.saveSite({
                        exit: false,
                        publish: false,
                        navigateTo: { isNew: true },
                        capturedPageId: routerParams.pageId,
                    }),
                ];
            })
        )
    );

    public readonly createNewPage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PagesActions.createNewPage),
            withLatestFrom(this.routerFacade.routerParams$, (_, routerParams: Params) => ({
                routerParams,
            })),
            switchMap(({ routerParams }) => {
                const page = this.pagesService.createEmptyPage();
                return [
                    PagesActions.createNewPageSuccess({ page: page as SitePageEntity }),
                    PagesActions.navigateSitePage({
                        siteId: routerParams.siteId,
                        pageId: page.id.toString(),
                    }),
                ];
            })
        )
    );

    public readonly activatePageRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PagesActions.activatePageRequest),
            withLatestFrom(
                this.routerFacade.routerParams$,
                ({ siteId, pageId, resetContent }, routerParams: Params) => ({
                    routerParams,
                    siteId,
                    pageId,
                    resetContent,
                })
            ),
            switchMap(({ siteId, pageId, routerParams, resetContent }) => {
                return this.pagesHttpService.activatePage(siteId, pageId).pipe(
                    withLatestFrom(this.siteFacade.site$, (page, site) => ({ site, page })),
                    mergeMap(({ site, page }) => {
                        if (this.siteService.isSitePublished(site)) {
                            return forkJoin({ site: of(site), page: of(page) });
                        }

                        return forkJoin({
                            site: this.siteHttpService.getSite(siteId),
                            page: of(page),
                        });
                    }),
                    withLatestFrom(
                        this.userFacade.isUserTariffFree$,
                        this.pagesFacade.isEduSite$,
                        this.appFacade.isEcommerceMode$,
                        // Latest value before `activatePageSuccess` is dispatched (which then marks the
                        // site as published and flips this to false), so it's `true` for the publish
                        // that completes the onboarding flow.
                        this.onboardingWizardAbTestService.showOnboardingSteps$
                    ),
                    switchMap(([{ site, page }, isFreeTariff, isEduSite, isEcommerceMode, isOnboardingWizard]) => {
                        const actions: Action[] = [
                            SiteActions.updateSite({ site }),
                            PagesActions.activatePageSuccess({
                                pageId,
                                page: this.pagesService.mapPageToEntityPage(page),
                            }),
                        ];

                        const modalHidden = localStorage.getItem(CTA_AFTER_PUBLISH_MODAL_STORAGE_KEY) === 'true';

                        const isCtaModal =
                            isFreeTariff &&
                            !modalHidden &&
                            page.content_source_id === EContentSources.Website &&
                            !isEduSite &&
                            !isEcommerceMode;

                        const showPaidAlert = isFreeTariff && page.content_source_id === EContentSources.Website;

                        if (isCtaModal || isOnboardingWizard) {
                            this.modalService.show(CtaAfterPublishModalComponent, {
                                ignoreBackdropClick: true,
                                initialState: { pageUrl: page.url, isOnboardingWizard, showPaidAlert },
                            });
                        } else {
                            actions.push(
                                NotifyAction.showNotify({
                                    toastType: 'success',
                                    message: this.translateService.instant('site_published'),
                                })
                            );
                        }

                        if (routerParams.pageId.toString() === pageId.toString() || resetContent) {
                            actions.push(ContentActions.resetContentToPublished());
                        }

                        return actions;
                    }),
                    catchError(({ message }: HttpErrorResponse) => [
                        PagesActions.activatePageFailure({ pageId, error: message }),
                    ])
                );
            })
        )
    );

    public readonly disablePageRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PagesActions.disablePageRequest),
            switchMap(({ siteId, pageId }) =>
                this.pagesHttpService.disablePage(siteId, pageId).pipe(
                    switchMap((page: SitePage) => [
                        PagesActions.disablePageSuccess({
                            pageId,
                            page: this.pagesService.mapPageToEntityPage(page),
                        }),
                    ]),
                    catchError(({ message }: HttpErrorResponse) => [
                        PagesActions.disablePageFailure({ pageId, error: message }),
                    ])
                )
            )
        )
    );

    public readonly deletePageRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PagesActions.deletePageRequest),
            switchMap(({ siteId, pageId }) =>
                this.pagesHttpService.deleteSitePage(siteId, pageId).pipe(
                    withLatestFrom(
                        this.routerFacade.routerParams$,
                        this.pagesFacade.pages$,
                        (_, routerParams: Params, pages) => ({
                            routerParams,
                            pages,
                        })
                    ),
                    switchMap(({ routerParams, pages }) => {
                        const actions: Action[] = [PagesActions.deletePageSuccess({ pageId })];

                        if (routerParams.pageId.toString() === pageId.toString()) {
                            actions.push(
                                PagesActions.navigateSitePage({
                                    siteId: siteId.toString(),
                                    pageId: pages[0].id.toString(),
                                })
                            );
                        }

                        return actions;
                    }),
                    catchError(({ message }: HttpErrorResponse) => [
                        PagesActions.deletePageFailure({ pageId, error: message }),
                    ])
                )
            )
        )
    );

    public readonly copyPageRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PagesActions.copyPageRequest),
            switchMap(({ siteId, pageId, copyRequest }) =>
                this.pagesHttpService.copyPage(siteId, pageId, copyRequest).pipe(
                    switchMap((page: SitePage) => [
                        PagesActions.copyPageSuccess({ pageId, page: this.pagesService.mapPageToEntityPage(page) }),
                    ]),
                    catchError(({ message }: HttpErrorResponse) => [
                        PagesActions.copyPageFailure({ pageId, error: message }),
                    ])
                )
            )
        )
    );

    public readonly copyPage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(PagesActions.copyPage),
                withLatestFrom(this.contentFacade.isDirty$, ({ siteId, page }, isDirty) => ({
                    siteId,
                    page,
                    isDirty,
                })),
                map(({ siteId, page, isDirty }) => {
                    if (!isDirty) {
                        return this.modalsFacade.showCopyPageModal(siteId, page);
                    }

                    const modalRef: BsModalRef<ExitPageModalComponent> = this.modalService.show(ExitPageModalComponent);

                    modalRef.content.onClose.subscribe((result: boolean) => {
                        if (!result) {
                            this.contentFacade.setIsDirty(false);
                            return this.modalsFacade.showCopyPageModal(siteId, page);
                        }

                        this.siteFacade.saveSiteRequest(false, false, page.id);
                        return this.modalsFacade.showCopyPageModal(siteId, page);
                    });
                })
            ),
        { dispatch: false }
    );

    public readonly updatePagesContent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.copyBlockToPageSuccess),
            withLatestFrom(this.routerFacade.routerParams$, (_, routerParams: Params) => routerParams),
            mergeMap((routerParams) => this.pagesHttpService.getSitePages(routerParams.siteId)),
            map((data) =>
                PagesActions.setPages({ pages: this.pagesService.mapPagesToEntityPages(data.pages), total: data.total })
            )
        )
    );

    public readonly loadMorePages$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PagesActions.loadMorePages),
            withLatestFrom(this.routerFacade.routerParams$, ({ page, perPage }, routerParams: Params) => ({
                page,
                perPage,
                routerParams,
            })),
            mergeMap(({ page, perPage, routerParams }) =>
                this.pagesHttpService.getSitePages(routerParams.siteId, { page: page, perPage: perPage }).pipe(
                    map((data) =>
                        PagesActions.loadMorePagesSuccess({
                            pages: data.pages || [],
                            total: data.total,
                        })
                    ),
                    catchError((error) => of(PagesActions.loadPagesFailure({ error })))
                )
            )
        )
    );
}
